/** Navigation and history signals that the PM thread view reads (#4668). */

// WebKit cancels in-flight requests when a same-tab link navigation starts, before
// pagehide. A link that never unloads the page delays a cancelled load's error this long.
export const PM_LINK_NAVIGATION_WINDOW_MS = 2000;

/**
 * Whether a click navigates this tab to another document: a plain primary click
 * on an http(s) link that no handler prevented, with no target or download.
 *
 * @param {MouseEvent} event a click whose dispatch is complete
 * @param {string} pageHref the address of this document
 * @returns {boolean}
 */
export function pmIsSameTabLinkNavigation(event, pageHref) {
    if (!event || event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

    const link = event.target && typeof event.target.closest === 'function'
        ? event.target.closest('a[href]')
        : null;
    if (!link) return false;

    const target = link.getAttribute('target');
    if ((target && target !== '_self') || link.hasAttribute('download')) return false;
    if (link.protocol !== 'http:' && link.protocol !== 'https:') return false;

    // A fragment link to this document scrolls and does not unload the page.
    const linkUrl = String(link.href).split('#');
    return !(linkUrl.length > 1 && linkUrl[0] === String(pageHref).split('#')[0]);
}

/**
 * Records the last click on `browserWindow`.
 *
 * @returns {{started: function(): boolean}} whether that click started a same-tab
 *   link navigation inside the window
 */
export function createPmLinkNavigationTracker(browserWindow) {
    let lastClick = null;

    // Capture phase, so a handler that stops propagation cannot hide the click. The
    // click is judged when asked, after every handler had its chance to prevent it.
    browserWindow.addEventListener('click', (event) => {
        lastClick = { event, at: Date.now() };
    }, true);
    // A restored page does not carry a navigation from before it left.
    browserWindow.addEventListener('pageshow', () => {
        lastClick = null;
    });

    return {
        started: () => lastClick !== null
            && Date.now() - lastClick.at < PM_LINK_NAVIGATION_WINDOW_MS
            && pmIsSameTabLinkNavigation(lastClick.event, browserWindow.location.href),
    };
}

/**
 * What a history entry does to the conversation pane: 'open', 'keep' or 'close'.
 * A server-rendered entry has no state, and its URL still names the conversation.
 *
 * @param {{state: *, urlThreadId: *, urlThreadListed: boolean, openThreadId: *}} entry
 *   state is history.state, urlThreadId the thread_id in the URL, urlThreadListed
 *   whether the thread list shows a row for it, and openThreadId the conversation in
 *   the pane. An absent id is any falsy value.
 * @returns {{action: string, threadId: ?string}}
 */
export function pmPopstateAction({ state, urlThreadId, urlThreadListed, openThreadId }) {
    if (state && typeof state.thread_id !== 'undefined' && state.thread_id !== '' && !isNaN(state.thread_id)) {
        return { action: 'open', threadId: String(state.thread_id) };
    }
    if (!urlThreadId) return { action: 'close', threadId: null };

    // A fragment link adds a stateless entry for the conversation that is already open.
    if (openThreadId && String(openThreadId) === String(urlThreadId)) {
        return { action: 'keep', threadId: String(urlThreadId) };
    }
    // A message request shows no row, and view_thread would open it without its request bar.
    if (!urlThreadListed) return { action: 'close', threadId: null };

    return { action: 'open', threadId: String(urlThreadId) };
}
